package pl.decks.personal.components.widgets.iconribbon

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.forms.ButtonVars
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.name
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.times
import pl.decks.personal.components.animations.AppearAnimation
import pl.decks.personal.components.animations.GrowHeightAnimation
import pl.decks.personal.components.animations.PushFromTopAnimation
import pl.decks.personal.components.animations.SlideLeftRightAnimation
import pl.decks.personal.components.widgets.button.IconButton
import pl.decks.personal.extensions.animationNotNull
import pl.decks.personal.extensions.blend

@Composable
fun IconRibbon(
    options: List<IconRibbonOption>,
    selectedOption: Int,
    onOptionSelected: (Int) -> Unit,
    initialVariant: IconRibbonVariant? = null,
    ribbonBackgroundColor: Color? = null,
    modifier: Modifier = Modifier,
) {
    var variant by remember { mutableStateOf(initialVariant ?: IconRibbonVariant.COLLAPSED) }
    var pendingOption by remember { mutableStateOf(selectedOption) }
    key(variant) {
        Box(
            IconRibbonStylesheet.Ribbon.toModifier()
                .minWidth(IconRibbonStylesheet.Vars.size.value() * (options.size + 0.5))
                .setVariable(IconRibbonStylesheet.Vars.accentColor, options[selectedOption].color)
                .setVariable(IconRibbonStylesheet.Vars.backgroundColor, ribbonBackgroundColor ?: Colors.Transparent)
                .cursor(if (variant == IconRibbonVariant.COLLAPSED) Cursor.Pointer else Cursor.Default)
                .onClick { if (variant == IconRibbonVariant.COLLAPSED) variant = IconRibbonVariant.EXPANDED }
                .then(modifier),
        ) {
            Box(
                IconRibbonStylesheet.TitleContainer.toModifier()
                    .setVariable(GrowHeightAnimation.Vars.factor, 1.25)
                    .setVariable(PushFromTopAnimation.Vars.toMargin, IconRibbonStylesheet.Vars.size.value() * 0.5)
                    .animationNotNull(
                        variant.animate(IconRibbonStylesheet.Anims.PushRightAndCenter, IconRibbonVariant.Phase.I),
                        variant.animate(GrowHeightAnimation.Frames, IconRibbonVariant.Phase.II),
                    )
                    .onAnimationEnd {
                        if (variant != IconRibbonVariant.COLLAPSING) return@onAnimationEnd
                        when (it.animationName) {
                            GrowHeightAnimation.Frames.name -> onOptionSelected(pendingOption)
                            IconRibbonStylesheet.Anims.PushRightAndCenter.name -> variant = IconRibbonVariant.COLLAPSED
                        }
                    },
                contentAlignment = Alignment.Center,
            ) {
                SpanText(
                    options[selectedOption].title,
                    modifier = IconRibbonStylesheet.Title.toModifier()
                        .animationNotNull(
                        variant.animate(PushFromTopAnimation.Frames, IconRibbonVariant.Phase.II)
                    )
                )
            }
            IconRibbonOptions(
                variant = variant,
                options = options,
                selectedOption = pendingOption,
                onOptionIconClick = {
                    variant = variant.next()
                    pendingOption = it
                },
                mixColor = ribbonBackgroundColor ?: if (ColorMode.current.isLight) Colors.White else Colors.Black,
            )
        }
    }
}

@Composable
private fun IconRibbonOptions(
    variant: IconRibbonVariant,
    options: List<IconRibbonOption>,
    selectedOption: Int,
    onOptionIconClick: (Int) -> Unit,
    mixColor: Color,
) = Box(
    IconRibbonStylesheet.OptionsContainer.toModifier().animationNotNull(
        variant.animate(IconRibbonStylesheet.Anims.PushRightAndElevate, IconRibbonVariant.Phase.I)
    )
) {
    options.forEachIndexed { position, option ->
        val isSelected = position == selectedOption
        if (variant != IconRibbonVariant.COLLAPSED || isSelected) {
            IconRibbonButton(
                onClick = { onOptionIconClick(position) },
                position = position,
                maxPosition = options.lastIndex,
                isSelected = isSelected,
                mixColor = mixColor,
                optionColor = option.color,
                modifier = Modifier.animationNotNull(
                    variant.animate(SlideLeftRightAnimation.Frames, IconRibbonVariant.Phase.II),
                    if (!isSelected) variant.animate(AppearAnimation.Frames, IconRibbonVariant.Phase.II) else null,
                )
            ) { option.icon() }
        }
    }
}

@Composable
private fun IconRibbonButton(
    onClick: () -> Unit,
    position: Int,
    maxPosition: Int,
    isSelected: Boolean,
    mixColor: Color,
    optionColor: Color,
    modifier: Modifier = Modifier,
    content: @Composable () -> Unit,
) = Box(
    IconRibbonStylesheet.OptionWrap.toModifier()
        .zIndex(if (isSelected) maxPosition + 1 else maxPosition - position)
        .setVariable(IconRibbonStylesheet.Vars.optionColor, optionColor)
        .setVariable(SlideLeftRightAnimation.Vars.toPosition, position * (100 / maxPosition).percent)
        .setVariable(ButtonVars.BackgroundDefaultColor, mixColor.blend(optionColor, if (isSelected) 0.2f else 0f))
        .setVariable(ButtonVars.BackgroundHoverColor, mixColor.blend(optionColor, 0.3f))
        .setVariable(ButtonVars.BackgroundPressedColor, mixColor.blend(optionColor, 0.4f))
        .then(modifier)
) {
    IconButton(onClick, variant = IconRibbonStylesheet.OptionButtonVariant) { content() }
}
